import React from "react";
import { Button, ChildrenProps, Column, Conditional, Else, If, Modal, Row } from "@hex-insights/core";
import { useActivePageRegistration } from "@hex-insights/router";
import { Tile } from "../../../../../../Components/Tile";
import {
	AcademicTerm,
	CourseSectionPeriodFilterInput,
	Student,
	StudentDetailQuery,
	truncateDisplayItems,
	useClassYearNamesQuery,
	useCourseSectionListQuery,
	useEmployeeNamesDescriptionsAndImagesQuery,
	useHomeRoomSectionNamesQuery,
} from "../../../../../../Utilities";
import { EmployeeFormat } from "../../../../../Format";
import { ClassYearLink, EmployeeLink, HomeRoomSectionLink } from "../../../../../Links";
import { AcademicTermUtilities } from "../../../../../Utilities";
import * as CourseSectionComponents from "../../../../CourseSection";
import * as CourseSectionPeriodComponents from "../../../../CourseSectionPeriod";
import * as EmployeeComponents from "../../../../Employee";
import { ControlledStudentEnrollmentStatusBadge } from "../../../Enrollment";
import { DetailRouteContext } from "../../RouteContext";
import { StudentAttendanceTile } from "./AttendanceTile";
import styles from "./styles.module.css";

export type AcademicsBodyProps = {
	student: StudentDetailQuery["student"];
};

export function AcademicsBody({ student }: AcademicsBodyProps) {
	const { subPageInfos } = React.useContext(DetailRouteContext);
	useActivePageRegistration(subPageInfos.academics);

	const { loading: loadingAcademicTerms, selectedAcademicTerm } = AcademicTermUtilities.useAcademicTermsNavigation();

	return (
		<Row justify="spaced-start">
			<Column justify="spaced-start" style={{ width: "25rem", flexShrink: 0 }}>
				<AttendanceSection student={student} />
				<ScheduleSection student={student} />
			</Column>
			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<Conditional>
					<If condition={loadingAcademicTerms && !selectedAcademicTerm}>Loading...</If>
					<Else>
						{!!selectedAcademicTerm && (
							<React.Fragment>
								<GeneralSection student={student} academicTerm={selectedAcademicTerm} />
								<CourseSectionsSection student={student} academicTerm={selectedAcademicTerm} />
							</React.Fragment>
						)}
					</Else>
				</Conditional>
			</Column>
		</Row>
	);
}

type PageSectionProps = {
	student: StudentDetailQuery["student"];
};

function AttendanceSection({ student }: PageSectionProps) {
	return <StudentAttendanceTile student={student} />;
}

function ScheduleSection({ student }: PageSectionProps) {
	const filters = React.useMemo<CourseSectionPeriodFilterInput>(
		() => ({
			courseSection: [{ studentCourseSectionEnrollments: [{ studentIDEQ: student.id }] }],
		}),
		[student.id],
	);

	return <CourseSectionPeriodComponents.ScheduleTile filters={filters} />;
}

type GeneralSectionProps = PageSectionProps & {
	academicTerm: Pick<AcademicTerm, "id">;
};

function GeneralSection({ student, academicTerm }: GeneralSectionProps) {
	// TODO (after adding term navigation) show class year at the time of the selected term
	const { loading: loadingClassYears, data: classYearsData } = useClassYearNamesQuery({
		variables: {
			filters: {
				yearsFromGraduationEQ: student.yearsFromGraduation,
			},
		},
		skip: student.graduationYear === null || student.yearsFromGraduation < 0,
	});
	const classYear = classYearsData?.classYearConnection.edges[0]?.node;

	const { loading: loadingHomeRoomSections, data: homeRoomSectionsData } = useHomeRoomSectionNamesQuery({
		variables: {
			filters: {
				academicTermIDEQ: academicTerm.id,
				studentHomeRoomSectionEnrollments: [{ studentIDEQ: student.id }],
			},
		},
	});

	return (
		<Tile>
			<Tile.Body>
				<Row align="center">
					<LabeledItem label="Enrollment Status">
						<ControlledStudentEnrollmentStatusBadge studentID={student.id} />
					</LabeledItem>

					<div className={styles["general-section__spacer"]}></div>

					<LabeledItem label="Class Year">
						<Conditional>
							<If condition={student.yearsFromGraduation < 0}>Graduated</If>
							<If condition={loadingClassYears && !classYearsData}>Loading...</If>
							<If condition={!classYear}>Class Year Unknown</If>
							<Else>
								{!!classYear && (
									<ClassYearLink instance={classYear} className="link--primary link--no-text-decoration" />
								)}
							</Else>
						</Conditional>
					</LabeledItem>

					<div className={styles["general-section__spacer"]}></div>

					<LabeledItem label="Home Room">
						<Conditional>
							<If condition={loadingHomeRoomSections && !homeRoomSectionsData}>Loading...</If>
							<If condition={!!homeRoomSectionsData && homeRoomSectionsData.homeRoomSectionConnection.edges.length > 0}>
								<Row justify="spaced-start">
									{homeRoomSectionsData?.homeRoomSectionConnection.edges.map(({ node }) => (
										<HomeRoomSectionLink
											key={node.id}
											instance={node}
											className="link--primary link--no-text-decoration"
										/>
									))}
								</Row>
							</If>
							<Else>None</Else>
						</Conditional>
					</LabeledItem>

					<div className={styles["general-section__spacer"]}></div>

					<GeneralSectionTeachers student={student} academicTerm={academicTerm} />
				</Row>
			</Tile.Body>
		</Tile>
	);
}

type GeneralSectionTeachersProps = {
	student: Pick<Student, "id">;
	academicTerm: Pick<AcademicTerm, "id">;
};

function GeneralSectionTeachers({ student, academicTerm }: GeneralSectionTeachersProps) {
	const { loading, data } = useEmployeeNamesDescriptionsAndImagesQuery({
		variables: {
			filters: {
				or: [
					{
						teacherHomeRoomSectionEnrollments: [
							{
								homeRoomSection: [
									{
										academicTermIDEQ: academicTerm.id,
										studentHomeRoomSectionEnrollments: [{ studentIDEQ: student.id }],
									},
								],
							},
						],
					},
					{
						teacherCourseSectionEnrollments: [
							{
								courseSection: [
									{
										academicTermIDEQ: academicTerm.id,
										studentCourseSectionEnrollments: [{ studentIDEQ: student.id }],
									},
								],
							},
						],
					},
				],
			},
		},
	});

	const { displayedItems, anyHidden, numHidden } = truncateDisplayItems(data?.employeeConnection.edges ?? [], 2);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();
	const teachers = React.useMemo(() => data?.employeeConnection.edges.map((e) => e.node), [data]);

	return (
		<React.Fragment>
			<LabeledItem label="Teachers">
				<Conditional>
					<If condition={loading && !data}>Loading...</If>
					<If condition={!!data && data.employeeConnection.edges.length > 0}>
						<Row justify="spaced-start" align="center">
							{displayedItems.map(({ node }) => (
								<EmployeeLink key={node.id} instance={node} className="link--primary link--no-text-decoration">
									<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
										<EmployeeComponents.EmployeeIcon employee={node} size={20} /> {EmployeeFormat.name(node)}
									</Row>
								</EmployeeLink>
							))}
							<If condition={anyHidden}>
								<Button
									variant="link"
									size="small"
									onClick={openModal}
									style={{ fontSize: "1rem", marginLeft: "-0.5rem" }}
								>
									and {numHidden} others
								</Button>
							</If>
						</Row>
					</If>
				</Conditional>
			</LabeledItem>

			<Modal.If condition={isModalOpen}>
				<EmployeeComponents.EmployeesListModal
					heading="Teachers"
					isLoading={loading}
					employees={teachers}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type LabeledItemProps = {
	label: string;
} & Partial<ChildrenProps>;

function LabeledItem({ label, children }: LabeledItemProps) {
	return (
		<Column justify="spaced-start" verticalSpacing="0.5rem">
			<span style={{ fontSize: "0.875rem", color: "var(--neutral-colors--secondary)" }}>{label}</span>
			{children}
		</Column>
	);
}

type CourseSectionsSectionProps = PageSectionProps & {
	academicTerm: Pick<AcademicTerm, "id">;
};

function CourseSectionsSection({ student, academicTerm }: CourseSectionsSectionProps) {
	const { loading, data } = useCourseSectionListQuery({
		variables: {
			filters: {
				studentCourseSectionEnrollments: [{ studentIDEQ: student.id }],
				academicTermIDEQ: academicTerm.id,
			},
		},
	});
	const courseSections = React.useMemo(() => data?.courseSectionConnection.edges.map((e) => e.node), [data]);

	return (
		<CourseSectionComponents.CourseSectionsPaginatedGridTile
			isLoading={loading}
			courseSections={courseSections}
			columns={2}
			rows={3}
		/>
	);
}
